import React, { useState } from "react";
import {
  Card,
  Text,
  Group,
  Button,
  Stack,
  Alert,
  CopyButton,
  Menu,
  SimpleGrid,
  ActionIcon
} from "@mantine/core";
import {
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandX,
  IconBrandTiktok,
  IconShare,
  IconDownload,
  IconClipboardCheck,
  IconInfoCircle,
  IconChevronDown,
  IconChevronUp
} from "@tabler/icons-react";
import { useMediaQuery, useToggle } from "@mantine/hooks";

// Style import
import styles from './social.shares.module.css';

type SocialSharePanelProps = {
  // base64 image for the result card: "data:image/png;base64,..."
  imageDataUrl: string;
  // main caption for IG/TikTok/X (you can make X shorter if you want)
  caption: string;
  // public URL to the result page (must have OG tags for FB)
  shareUrl: string;
  // optional: hashtags for X
  xHashtags?: string[];
};

function dataUrlToFile(dataUrl: string, filename: string) {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export const SocialSharePanel: React.FC<SocialSharePanelProps> = ({
  imageDataUrl,
  caption,
  shareUrl,
  xHashtags = [],
}) => {
  const inCondense = useMediaQuery('(max-width: 48em)');
  const [mobileDrawer, toggleDrawer] = useToggle();
  const [igError, setIgError] = useState<string | null>(null);

  // ===== Instagram =====
  const handleInstagramShare = async () => {
    try {
      const file = dataUrlToFile(imageDataUrl, "myyenta-result.png");
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          text: caption,
          title: "MyYenta Personality Result",
        });
        setIgError(null);
      } else {
        setIgError(
          "This browser/device can’t share the image directly. Use Save + Copy below."
        );
      }
    } catch (e) {
      console.error(e);
      setIgError("Sharing failed. Use Save + Copy below.");
    }
  };

  const handleDownloadImage = () => {
    const a = document.createElement("a");
    a.href = imageDataUrl;
    a.download = "myyenta-result.png";
    a.click();
  };

  // ===== Facebook =====
  const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl
  )}`;
  const handleFacebookShare = () => {
    window.open(
      fbShareUrl,
      "_blank",
      "noopener,noreferrer,width=700,height=600"
    );
  };

  // ===== X (Twitter) =====
  const buildXIntent = () => {
    const base = "https://twitter.com/intent/tweet";
    const params = new URLSearchParams();
    params.set("text", caption);
    params.set("url", shareUrl);
    if (xHashtags.length > 0) {
      params.set("hashtags", xHashtags.join(","));
    }
    return `${base}?${params.toString()}`;
  };
  const handleXShare = () => {
    window.open(buildXIntent(), "_blank", "noopener,noreferrer");
  };

  // ===== TikTok (web fallback) =====
  // TikTok’s official “Share Kit” is for native apps, so on PWA we just help the user.
  // We can still open TikTok web / app link to nudge them.
  const handleOpenTikTok = () => {
    // simple approach: open TikTok
    window.open("https://www.tiktok.com/upload?lang=en", "_blank");
  };

  const showShareStack = () => {
    if(inCondense && mobileDrawer) return true;
    else if(inCondense) return false;
    return true; // Absolute default should e true
  }

  return (
    <Card shadow="0" styles={{ root: { borderWidth: '1.5px'}}} withBorder bg={'transparent'} p={12} mb={8}>
      <Stack gap="md">
        <div onClick={() => toggleDrawer()}>
          <Text fw={600} size="lg">
            Share your result
          </Text>
          <Text size="sm" c="dimmed">
            Post your personality match and invite friends to take the test.
          </Text>
          {inCondense && <ActionIcon variant="outline" radius={32} className={styles.openCloseBtn} onClick={() => {}}>
            {mobileDrawer ? <IconChevronUp /> : <IconChevronDown />}
          </ActionIcon>}
        </div>

        {showShareStack() && <SimpleGrid cols={{base: 1, sm: 4, md: 4, lg: 4}}>
          {/* Instagram */}
          <Menu shadow="md" width={200}>
            <Group gap={0} style={{ flex: 1 }}>
              <Button
                leftSection={<IconBrandInstagram size={18} />}
                variant="light"
                color="pink"
                onClick={handleInstagramShare}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  flex: 1,
                }}
              >
                Share
              </Button>
              <Menu.Target>
                <Button
                  variant="light"
                  color="pink"
                  px="xs"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <IconChevronDown size={14} />
                </Button>
              </Menu.Target>
            </Group>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconShare size={16} />}
                onClick={handleInstagramShare}
              >
                Share (Web Share)
              </Menu.Item>
              <Menu.Item
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadImage}
              >
                Save image
              </Menu.Item>
              <CopyButton value={caption} timeout={2000}>
                {({ copy, copied }) => (
                  <Menu.Item
                    leftSection={<IconClipboardCheck size={16} />}
                    onClick={copy}
                    color={copied ? "teal" : undefined}
                  >
                    {copied ? "Caption copied" : "Copy caption"}
                  </Menu.Item>
                )}
              </CopyButton>
            </Menu.Dropdown>
          </Menu>

          {/* Facebook */}
          <Menu shadow="md" width={200}>
            <Group gap={0} style={{ flex: 1 }}>
              <Button
                leftSection={<IconBrandFacebook size={18} />}
                variant="light"
                color="blue"
                onClick={handleFacebookShare}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  flex: 1,
                }}
              >
                Share
              </Button>
              <Menu.Target>
                <Button
                  variant="light"
                  color="blue"
                  px="xs"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <IconChevronDown size={14} />
                </Button>
              </Menu.Target>
            </Group>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconBrandFacebook size={16} />}
                onClick={handleFacebookShare}
              >
                Share to Facebook
              </Menu.Item>
              <CopyButton value={shareUrl} timeout={2000}>
                {({ copy, copied }) => (
                  <Menu.Item
                    leftSection={<IconClipboardCheck size={16} />}
                    onClick={copy}
                    color={copied ? "teal" : undefined}
                  >
                    {copied ? "Link copied" : "Copy link"}
                  </Menu.Item>
                )}
              </CopyButton>
              <Menu.Divider />
              <Menu.Label>
                <Text size="xs" c="dimmed">
                  Facebook will pull the image/title from your page's Open Graph tags.
                </Text>
              </Menu.Label>
            </Menu.Dropdown>
          </Menu>

          {/* X (Twitter) */}
          <Menu shadow="md" width={200}>
            <Group gap={0} style={{ flex: 1 }}>
              <Button
                leftSection={<IconBrandX size={18} />}
                variant="light"
                color="dark"
                onClick={handleXShare}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  flex: 1,
                }}
              >
                Share
              </Button>
              <Menu.Target>
                <Button
                  variant="light"
                  color="dark"
                  px="xs"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <IconChevronDown size={14} />
                </Button>
              </Menu.Target>
            </Group>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconBrandX size={16} />}
                onClick={handleXShare}
              >
                Post on X
              </Menu.Item>
              <CopyButton value={caption + " " + shareUrl} timeout={2000}>
                {({ copy, copied }) => (
                  <Menu.Item
                    leftSection={<IconClipboardCheck size={16} />}
                    onClick={copy}
                    color={copied ? "teal" : undefined}
                  >
                    {copied ? "Text copied" : "Copy text"}
                  </Menu.Item>
                )}
              </CopyButton>
              <Menu.Divider />
              <Menu.Label>
                <Text size="xs" c="dimmed">
                  Uses X's web intent to prefill the tweet.
                </Text>
              </Menu.Label>
            </Menu.Dropdown>
          </Menu>

          {/* TikTok */}
          <Menu shadow="md" width={200}>
            <Group gap={0} style={{ flex: 1 }}>
              <Button
                leftSection={<IconBrandTiktok size={18} />}
                variant="light"
                color="dark"
                onClick={handleDownloadImage}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  flex: 1,
                }}
              >
                Save
              </Button>
              <Menu.Target>
                <Button
                  variant="light"
                  color="dark"
                  px="xs"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <IconChevronDown size={14} />
                </Button>
              </Menu.Target>
            </Group>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadImage}
              >
                Save image/video
              </Menu.Item>
              <CopyButton value={caption} timeout={2000}>
                {({ copy, copied }) => (
                  <Menu.Item
                    leftSection={<IconClipboardCheck size={16} />}
                    onClick={copy}
                    color={copied ? "teal" : undefined}
                  >
                    {copied ? "Caption copied" : "Copy caption"}
                  </Menu.Item>
                )}
              </CopyButton>
              <Menu.Item
                leftSection={<IconBrandTiktok size={16} />}
                onClick={handleOpenTikTok}
              >
                Open TikTok
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </SimpleGrid>}

        {igError && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="yellow"
            variant="light"
            title="Instagram Fallback"
          >
            {igError}
            <br />
            1) Save image → 2) open Instagram → 3) create post/story → 4) paste caption.
          </Alert>
        )}
      </Stack>
    </Card>
  );
};
